import Weekday, { getIndexFromWeekday } from "./weekday";

export default interface TimeSpan {
  hours: number;
  minutes: number;
}

export const getHoursFromString = (timeSpanString: string) => {
  const hoursString = timeSpanString.split(":")[0];
  return parseInt(hoursString, 10);
};

export const getMinutesFromString = (timeSpanString: string) => {
  const minutesString = timeSpanString.split(":")[1];
  return parseInt(minutesString, 10);
};

export const getTimeSpanDifferenceDecimalHours = (timeFrom: TimeSpan, timeTo: TimeSpan) => {
  let difference = timeTo.hours - timeFrom.hours;
  difference += (timeTo.minutes - timeFrom.minutes) / 60;
  return difference;
};

export const getTimeSpanDecimalHours = (timeSpan: TimeSpan) => {
  return timeSpan.hours + timeSpan.minutes / 60;
};

export const getDateTimeDecimal = (time: TimeSpan, weekday: Weekday) =>
  getTimeSpanDecimalHours(time) + (getIndexFromWeekday(weekday) - 1) * 24;

export const getStringFromTimeSpan = (timeSpan: TimeSpan) => {
  return `${timeSpan.hours.toString().padStart(2, "0")}:${timeSpan.minutes.toString().padStart(2, "0")}`;
};

export const getTimeSpanFromString = (timeSpanString: string): TimeSpan => {
  return { hours: getHoursFromString(timeSpanString), minutes: getMinutesFromString(timeSpanString) };
};

export const getTimeSpanFromDecimal = (decimalNumber: number, isDuration?: boolean): TimeSpan | null => {
  let hours = Math.floor(decimalNumber);
  let minutes = Math.round((decimalNumber - Math.floor(decimalNumber)) * 60);
  if (minutes === 60) {
    hours += 1;
    minutes = 0;
  }

  if (isDuration) {
    return { hours, minutes };
  }

  if (decimalNumber < 0 || decimalNumber > 24) {
    return null;
  }
  return hours === 24 ? { hours: 23, minutes: 59 } : { hours, minutes };
};

export const increaseTimeSpanCappedAt24 = (timeSpan: TimeSpan, increaseValue: TimeSpan) => {
  let increasedTimeSpanDecimal = getTimeSpanDecimalHours(timeSpan) + getTimeSpanDecimalHours(increaseValue);
  const increasedTimeSpan = getTimeSpanFromDecimal(increasedTimeSpanDecimal);
  return increasedTimeSpan == null
    ? ({ hours: 23, minutes: 59 } as TimeSpan)
    : { hours: Math.round(increasedTimeSpan.hours), minutes: Math.round(increasedTimeSpan.minutes) };
};

export const timeShiftWithOverflow = (timeSpan: TimeSpan, changeValue: number) => {
  const changedTimeSpanDecimal = Math.round((getTimeSpanDecimalHours(timeSpan) + changeValue) * 2) / 2;
  if (changedTimeSpanDecimal < 24 && changedTimeSpanDecimal >= 0) {
    return { timespan: getTimeSpanFromDecimal(changedTimeSpanDecimal), dayShift: 0 };
  }
  if (changedTimeSpanDecimal === 24) {
    return { timespan: { hours: 23, minutes: 59 } as TimeSpan, dayShift: 0 };
  } else if (changedTimeSpanDecimal < 0) {
    return { timespan: getTimeSpanFromDecimal(changedTimeSpanDecimal + 24), dayShift: -1 };
  } else {
    return { timespan: getTimeSpanFromDecimal(changedTimeSpanDecimal - 24), dayShift: 1 };
  }
};
